// Pure, dependency-free math for the trail flyover (no mapbox/turf/DOM imports), so it is unit-testable.

/**
 * Linearly interpolate elevation (metres) at a distance `distKm` along a 3D coordinate array
 * `coords3d` ([lng, lat, elev][]), given the precomputed cumulative distances `cumulativeDistKm`.
 * Clamps to the final elevation at/after the end of the line.
 */
export function getElevationAtDistanceKm(
  distKm: number,
  coords3d: number[][],
  cumulativeDistKm: number[]
): number {
  if (distKm >= cumulativeDistKm[cumulativeDistKm.length - 1]) {
    return coords3d[coords3d.length - 1][2];
  }
  for (let i = 1; i < cumulativeDistKm.length; i++) {
    if (cumulativeDistKm[i] >= distKm) {
      const span = cumulativeDistKm[i] - cumulativeDistKm[i - 1];
      const fraction = span > 0 ? (distKm - cumulativeDistKm[i - 1]) / span : 0;
      return coords3d[i - 1][2] + (coords3d[i][2] - coords3d[i - 1][2]) * fraction;
    }
  }
  return coords3d[coords3d.length - 1][2];
}

/**
 * Frame-rate-independent exponential smoothing factor for a timestep `dtSeconds` and time constant
 * `tauSeconds`. Returns a value in [0, 1]: 0 = keep the previous value, 1 = jump to the target.
 * Using `1 - e^(-dt/tau)` keeps the smoothing consistent regardless of frame rate (a longer frame
 * moves proportionally further toward the target than a short one).
 */
export function exponentialSmoothingAlpha(dtSeconds: number, tauSeconds: number): number {
  if (tauSeconds <= 0) return 1;
  if (dtSeconds <= 0) return 0;
  return 1 - Math.exp(-dtSeconds / tauSeconds);
}

/**
 * Advances normalized flyover progress (0..1) by one timestep. `elapsedMs` is the frame's wall-clock
 * delta (live playback) or a fixed step such as 1000/fps (deterministic headless rendering); `durationMs`
 * is the flyover's 1x duration and `speedMultiplier` scales playback speed. Clamps to 1, and treats a
 * non-positive duration as "already complete" so a zero-length route can't divide by zero.
 */
export function advanceProgress(
  currentT: number,
  elapsedMs: number,
  durationMs: number,
  speedMultiplier = 1
): number {
  if (durationMs <= 0) return 1;
  const next = currentT + (elapsedMs / durationMs) * speedMultiplier;
  return Math.min(Math.max(next, 0), 1);
}

/**
 * Lands one frame's progress step on the next stop it would cross, so the flight halts exactly AT a
 * photo instead of a frame past it. `stops` are ascending 0..1 fractions and `nextStop` is the index of
 * the first one not yet landed on. Stops already behind `currentT` are skipped, never landed on late.
 * Returns the (possibly shortened) progress, the stop landed on (or null), and the next index.
 */
export function landOnStop(
  currentT: number,
  nextT: number,
  stops: number[],
  nextStop: number
): { t: number; stop: number | null; nextStop: number } {
  let i = nextStop;
  while (i < stops.length && stops[i] <= currentT) i++;
  if (i < stops.length && nextT >= stops[i]) return { t: stops[i], stop: i, nextStop: i + 1 };
  return { t: nextT, stop: null, nextStop: i };
}

/** The live flyover's progress through its photo stops, between two frames. */
export interface LiveStopState {
  t: number;
  nextStop: number;
  /** The stop being held, or -1 while flying. */
  holdIndex: number;
  holdElapsedMs: number;
}

/**
 * One live frame through the photo stops: while a stop is held, the time just counts up (progress stays
 * put) until its hold is over; otherwise progress moves to `advancedT`, landing on the next stop it would
 * cross and starting that stop's hold. `holdMs[i]` is how long stop i holds (0 = don't stop). Counting in
 * play time rather than wall time is what makes a pause freeze the hold.
 */
export function stepLiveStops(
  state: LiveStopState,
  elapsedMs: number,
  advancedT: number,
  stops: number[],
  holdMs: number[]
): LiveStopState {
  if (state.holdIndex >= 0) {
    const held = state.holdElapsedMs + elapsedMs;
    if (held < (holdMs[state.holdIndex] || 0)) return { ...state, holdElapsedMs: held };
    // The hold is over; this frame flies again (from where it stopped, not a frame late).
    return { ...state, holdIndex: -1, holdElapsedMs: 0 };
  }
  const landed = landOnStop(state.t, advancedT, stops, state.nextStop);
  const holds = landed.stop !== null && (holdMs[landed.stop] || 0) > 0;
  return { t: landed.t, nextStop: landed.nextStop, holdIndex: holds ? landed.stop! : -1, holdElapsedMs: 0 };
}

/**
 * The index of the first stop still ahead of progress `t` — where a seek (fast-forward, rewind) should
 * pick the stops back up, so a rewind replays the stops it went back over and a skip never lands late.
 */
export function nextStopAfter(t: number, stops: number[]): number {
  const i = stops.findIndex((s) => s > t);
  return i < 0 ? stops.length : i;
}

/** Linear interpolation between `a` and `b` by `t` (0..1). */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Ease-in-out cubic (slow start, slow end) — approximates Mapbox's default easeTo curve, used to
 * drive the deterministic intro/outro so the rendered open/close feels like the live `easeTo`.
 */
export function easeInOutCubic(t: number): number {
  const c = Math.min(Math.max(t, 0), 1);
  return c < 0.5 ? 4 * c * c * c : 1 - Math.pow(-2 * c + 2, 3) / 2;
}

/**
 * Interpolate a bearing from `a` to `b` along the SHORTEST angular path (e.g. 350° -> 10° goes +20°,
 * not -340°), matching how Mapbox rotates the camera. Result is not normalized to [0,360); callers
 * pass it straight to the map.
 */
export function lerpAngleShortest(a: number, b: number, t: number): number {
  const delta = (((b - a) % 360) + 540) % 360 - 180;
  return a + delta * t;
}

export interface FlyoverCamera {
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
}

/**
 * Interpolate a full camera pose from `from` to `to` by `e` (0..1, already eased). Centre and zoom
 * are linear; bearing takes the shortest angular path. Drives the intro (flat 2D -> 3D tilt) and
 * outro (flyover end -> top-down overview) frames in headless rendering.
 */
export function interpolateCamera(from: FlyoverCamera, to: FlyoverCamera, e: number): FlyoverCamera {
  return {
    center: [lerp(from.center[0], to.center[0], e), lerp(from.center[1], to.center[1], e)],
    zoom: lerp(from.zoom, to.zoom, e),
    bearing: lerpAngleShortest(from.bearing, to.bearing, e),
    pitch: lerp(from.pitch, to.pitch, e)
  };
}

/**
 * Eases a [lng, lat] position toward `target` by `alpha` (0..1). Used to smooth the flyover camera
 * centre so it rounds corners instead of snapping its pan direction at every route vertex; the
 * marker and drawn line still sit on the true head.
 */
export function smoothLngLat(
  prev: [number, number],
  target: [number, number],
  alpha: number
): [number, number] {
  return [
    prev[0] + (target[0] - prev[0]) * alpha,
    prev[1] + (target[1] - prev[1]) * alpha
  ];
}

// Web-Mercator metres-per-pixel at zoom 0 for Mapbox's 512px tiles: equatorial circumference / 512.
const MERCATOR_METERS_PER_PIXEL_Z0 = 78271.51696;
const METERS_PER_DEGREE = 111320.0;

/** Web-Mercator metres per CSS pixel at `latitudeDegrees` and `zoom` (Mapbox 512px tiles). */
export function metersPerPixel(latitudeDegrees: number, zoom: number): number {
  return (MERCATOR_METERS_PER_PIXEL_Z0 * Math.cos((latitudeDegrees * Math.PI) / 180)) / 2 ** zoom;
}

/** Fast equirectangular metres between two [lng, lat] points — accurate at flyover scale. */
export function approxDistanceMeters(a: [number, number], b: [number, number]): number {
  const dLatMeters = (b[1] - a[1]) * METERS_PER_DEGREE;
  const dLngMeters = (b[0] - a[0]) * METERS_PER_DEGREE * Math.cos((a[1] * Math.PI) / 180);
  return Math.hypot(dLatMeters, dLngMeters);
}

/**
 * Pulls the eased camera `centre` forward toward `head` so it trails by at most `maxLagMeters`.
 *
 * Easing alone assumes the head moves continuously. It doesn't: a hike assembled from several
 * routes has gaps the marker steps over in one frame (see flyoverRoute.ts), and the eased centre
 * then glides the whole way after it — which reads as the camera flying off to the start of the
 * next route instead of a flyover. Capping the lag turns that into what the mobile apps do: the
 * camera is at the new head within a frame, and only the last screen-ish of travel eases.
 *
 * Returns `centre` untouched when it is already inside the cap, so the smooth pan at normal
 * flyover speed is unchanged; a non-positive cap disables it. Mirrors Android's
 * `FlyoverFraming.cappedCentre`.
 */
export function cappedCentre(
  centre: [number, number],
  head: [number, number],
  maxLagMeters: number
): [number, number] {
  if (!(maxLagMeters > 0)) return centre;
  const lagMeters = approxDistanceMeters(centre, head);
  if (lagMeters <= maxLagMeters) return centre;
  const pull = 1 - maxLagMeters / lagMeters;
  return [centre[0] + (head[0] - centre[0]) * pull, centre[1] + (head[1] - centre[1]) * pull];
}
